import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData } from '@tanstack/react-query';
import { Table, Space, Tag, Input, Select, Switch, Spin } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useWisecureQuery } from '@wisecure/api-client/hooks';
import { queryKeys } from '@/api/query-keys';
import { PageHeader } from '@/components/PageHeader';
import { neutral, success, warning, error, info, primary } from '@/theme';
import type { InterventionListPaginatedResponse, InterventionListItemResponse, CurrencyAmount } from '@wisecure/api-client';

const STATUS_OPTIONS = [
  { value: 'REQUESTED', label: 'Demandé' },
  { value: 'WAITING_FOR_AGENT_ACCEPTATION', label: 'En attente d\'acceptation' },
  { value: 'ACCEPTED', label: 'Accepté' },
  { value: 'PAYMENT_IN_PROGRESS', label: 'Paiement en cours' },
  { value: 'PAYMENT_DONE', label: 'Paiement effectué' },
  { value: 'PAYMENT_FAILED', label: 'Paiement échoué' },
  { value: 'AGENT_MOVING', label: 'Agent en déplacement' },
  { value: 'INSPECTION_IN_PROGRESS', label: 'Inspection en cours' },
  { value: 'NO_EFFRACTION', label: 'Pas d\'effraction' },
  { value: 'EFFRACTION', label: 'Effraction' },
  { value: 'SURVEILLANCE_IN_PROGRESS', label: 'Surveillance en cours' },
  { value: 'SURVEILLANCE_DONE', label: 'Surveillance terminée' },
  { value: 'REPORT_IN_PROGRESS', label: 'Rapport en cours' },
  { value: 'DONE', label: 'Terminé' },
  { value: 'CANCELED', label: 'Annulé' },
  { value: 'REJECTED', label: 'Refusé' },
  { value: 'PRICE_PROPOSED', label: 'Prix proposé' },
  { value: 'PRICE_ACCEPTED', label: 'Prix accepté' },
];

const STATUS_COLORS: Record<string, string> = {
  REQUESTED: warning[300],
  WAITING_FOR_AGENT_ACCEPTATION: warning[400],
  ACCEPTED: info[400],
  PAYMENT_IN_PROGRESS: info[300],
  PAYMENT_DONE: success[400],
  PAYMENT_FAILED: error[400],
  PAYMENT_TIMED_OUT: error[300],
  AGENT_MOVING: primary[300],
  INSPECTION_IN_PROGRESS: primary[400],
  NO_EFFRACTION: success[300],
  EFFRACTION: error[400],
  NO_SURVEILLANCE: neutral[300],
  SURVEILLANCE_IN_PROGRESS: info[400],
  SURVEILLANCE_DONE: success[400],
  SURVEILLANCE_PAYMENT_IN_PROGRESS: info[300],
  SURVEILLANCE_PAYMENT_DONE: success[300],
  SURVEILLANCE_PAYMENT_FAILED: error[400],
  SURVEILLANCE_PAYMENT_TIMED_OUT: error[300],
  REPORT_IN_PROGRESS: primary[300],
  DONE: success[500],
  CANCELED: neutral[300],
  REJECTED: error[400],
  HIDDEN: neutral[200],
  PRICE_PROPOSED: warning[300],
  PRICE_ACCEPTED: success[300],
};

const STATUS_LABELS: Record<string, string> = {
  REQUESTED: 'Demandé',
  WAITING_FOR_AGENT_ACCEPTATION: 'En attente',
  ACCEPTED: 'Accepté',
  PAYMENT_IN_PROGRESS: 'Paiement en cours',
  PAYMENT_DONE: 'Payé',
  PAYMENT_FAILED: 'Paiement échoué',
  PAYMENT_TIMED_OUT: 'Paiement expiré',
  AGENT_MOVING: 'En déplacement',
  INSPECTION_IN_PROGRESS: 'Inspection',
  NO_EFFRACTION: 'Pas d\'effraction',
  EFFRACTION: 'Effraction',
  NO_SURVEILLANCE: 'Pas de surveillance',
  SURVEILLANCE_IN_PROGRESS: 'Surveillance',
  SURVEILLANCE_DONE: 'Surveillance terminée',
  SURVEILLANCE_PAYMENT_IN_PROGRESS: 'Paiement surveillance',
  SURVEILLANCE_PAYMENT_DONE: 'Surveillance payée',
  SURVEILLANCE_PAYMENT_FAILED: 'Paiement surveillance échoué',
  SURVEILLANCE_PAYMENT_TIMED_OUT: 'Paiement surveillance expiré',
  REPORT_IN_PROGRESS: 'Rapport en cours',
  DONE: 'Terminé',
  CANCELED: 'Annulé',
  REJECTED: 'Refusé',
  HIDDEN: 'Masqué',
  PRICE_PROPOSED: 'Prix proposé',
  PRICE_ACCEPTED: 'Prix accepté',
};

function formatPrice(amount?: CurrencyAmount): string {
  if (!amount) return '—';
  return `${amount.amount.toFixed(2)} ${amount.symbol}`;
}

function formatPhone(phone?: { countryCode?: unknown; number?: unknown; fullNumber?: unknown }): string {
  if (!phone) return '—';
  if (phone.fullNumber) return String(phone.fullNumber);
  if (phone.number) return String(phone.number);
  return '—';
}

export function InterventionsPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [societyIdFilter, setSocietyIdFilter] = useState<string | undefined>(undefined);
  const [activeOnly, setActiveOnly] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const queryParams = {
    page,
    limit,
    ...(search ? { search } : {}),
    ...(statusFilter ? { status: statusFilter } : {}),
    ...(societyIdFilter ? { societyId: societyIdFilter } : {}),
    ...(activeOnly ? { isArchived: false } : {}),
    ...(showArchived ? {} : { isArchived: false }),
  };

  const { data, isLoading, isFetching } = useWisecureQuery<InterventionListPaginatedResponse>(
    [...queryKeys.dashboardInterventions, queryParams],
    (client) =>
      client.interventions.interventionsControllerFindAll({
        page,
        limit,
        ...(search ? { search } : {}),
        ...(statusFilter ? { status: statusFilter as 'REQUESTED' | 'WAITING_FOR_AGENT_ACCEPTATION' | 'ACCEPTED' | 'PAYMENT_IN_PROGRESS' | 'PAYMENT_DONE' | 'PAYMENT_FAILED' | 'PAYMENT_TIMED_OUT' | 'AGENT_MOVING' | 'INSPECTION_IN_PROGRESS' | 'NO_EFFRACTION' | 'EFFRACTION' | 'NO_SURVEILLANCE' | 'SURVEILLANCE_IN_PROGRESS' | 'SURVEILLANCE_DONE' | 'REPORT_IN_PROGRESS' | 'REJECTED' | 'CANCELED' | 'HIDDEN' | 'DONE' | 'PRICE_PROPOSED' | 'PRICE_ACCEPTED' | 'SURVEILLANCE_PAYMENT_IN_PROGRESS' | 'SURVEILLANCE_PAYMENT_DONE' | 'SURVEILLANCE_PAYMENT_FAILED' | 'SURVEILLANCE_PAYMENT_TIMED_OUT' } : {}),
        ...(societyIdFilter ? { societyId: societyIdFilter } : {}),
        ...(activeOnly ? { isArchived: false } : {}),
        ...(showArchived ? {} : { isArchived: false }),
      }) as Promise<InterventionListPaginatedResponse>,
    { placeholderData: keepPreviousData },
  );

  const handleTableChange = useCallback((pagination: { current?: number; pageSize?: number }) => {
    if (pagination.current) setPage(pagination.current);
    if (pagination.pageSize) setLimit(pagination.pageSize);
  }, []);

  const columns: ColumnsType<InterventionListItemResponse> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 100,
      render: (id: string) => (
        <span style={{ fontFamily: 'monospace', fontSize: 12, color: neutral[500] }}>
          {id.slice(0, 8)}...
        </span>
      ),
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      width: 160,
      render: (status: string) => {
        const color = STATUS_COLORS[status] ?? neutral[300];
        const label = STATUS_LABELS[status] ?? status;
        return (
          <Tag
            style={{
              backgroundColor: `${color}14`,
              color,
              borderColor: `${color}40`,
              borderRadius: 100,
              fontWeight: 500,
              fontSize: 12,
            }}
          >
            {label}
          </Tag>
        );
      },
    },
    {
      title: 'Client',
      key: 'client',
      width: 180,
      render: (_: unknown, record: InterventionListItemResponse) => {
        const client = (record as Record<string, unknown>).client as Record<string, unknown> | undefined;
        if (!client) return '—';
        const profile = client.clientProfile as Record<string, unknown> | undefined;
        const name = [profile?.firstName, profile?.lastName].filter(Boolean).join(' ') || '—';
        const phone = formatPhone(profile?.phone as { countryCode?: unknown; number?: unknown; fullNumber?: unknown } | undefined);
        return (
          <div>
            <div style={{ fontWeight: 500, fontSize: 13 }}>{name}</div>
            <div style={{ fontSize: 12, color: neutral[300] }}>{phone}</div>
          </div>
        );
      },
    },
    {
      title: 'Agent',
      key: 'agent',
      width: 180,
      render: (_: unknown, record: InterventionListItemResponse) => {
        if (!record.agent) return '—';
        const name = [record.agent.agentProfile?.firstName, record.agent.agentProfile?.lastName].filter(Boolean).join(' ') || '—';
        const phone = formatPhone(record.agent.agentProfile?.phone);
        return (
          <div>
            <div style={{ fontWeight: 500, fontSize: 13 }}>{name}</div>
            <div style={{ fontSize: 12, color: neutral[300] }}>{phone}</div>
          </div>
        );
      },
    },
    {
      title: 'Société',
      dataIndex: 'societyName',
      key: 'societyName',
      width: 150,
      render: (name: string | undefined) => name ?? '—',
    },
    {
      title: 'Adresse',
      dataIndex: 'locationAddress',
      key: 'locationAddress',
      width: 200,
      ellipsis: true,
      render: (addr: string) => addr || '—',
    },
    {
      title: 'Prix',
      key: 'price',
      width: 120,
      render: (_: unknown, record: InterventionListItemResponse) => formatPrice(record.agreedTotalAmount ?? record.proposedPrice),
    },
    {
      title: 'Créé le',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date: string) => new Date(date).toLocaleDateString('fr-FR'),
    },
    {
      title: 'Durée',
      key: 'duration',
      width: 100,
      render: (_: unknown, record: InterventionListItemResponse) => {
        const detail = record as Record<string, unknown>;
        const durationSeconds = detail.durationSeconds as number | undefined;
        if (durationSeconds) {
          const mins = Math.round(durationSeconds / 60);
          if (mins >= 60) {
            const h = Math.floor(mins / 60);
            const m = mins % 60;
            return `${h}h${m > 0 ? ` ${m}m` : ''}`;
          }
          return `${mins} min`;
        }
        return '—';
      },
    },
  ];

  return (
    <div>
      <PageHeader
        title="Interventions"
        subtitle={`${data?.total ?? 0} intervention(s)`}
        extra={isFetching ? <Spin size="small" /> : undefined}
      />

      <Space style={{ marginBottom: 16 }} wrap>
        <Input.Search
          placeholder="Rechercher par adresse, agent, société..."
          allowClear
          onSearch={(value) => { setSearch(value); setPage(1); }}
          onChange={(e) => { if (!e.target.value) { setSearch(''); setPage(1); } }}
          style={{ width: 300 }}
        />
        <Select
          value={statusFilter}
          onChange={(v) => { setStatusFilter(v); setPage(1); }}
          options={[{ value: undefined as unknown as string, label: 'Tous les statuts' }, ...STATUS_OPTIONS]}
          allowClear
          placeholder="Statut"
          style={{ width: 220 }}
        />
        <Input.Search
          placeholder="ID Société..."
          allowClear
          onSearch={(value) => { setSocietyIdFilter(value || undefined); setPage(1); }}
          onChange={(e) => { if (!e.target.value) { setSocietyIdFilter(undefined); setPage(1); } }}
          style={{ width: 180 }}
        />
        <Space size={8}>
          <span style={{ fontSize: 13, color: neutral[500] }}>Actifs uniquement</span>
          <Switch
            checked={activeOnly}
            onChange={(v) => { setActiveOnly(v); if (v) setShowArchived(false); setPage(1); }}
            size="small"
          />
        </Space>
        <Space size={8}>
          <span style={{ fontSize: 13, color: neutral[500] }}>Inclure archivés</span>
          <Switch
            checked={showArchived}
            onChange={(v) => { setShowArchived(v); if (v) setActiveOnly(false); setPage(1); }}
            size="small"
          />
        </Space>
      </Space>

      <Table<InterventionListItemResponse>
        columns={columns}
        dataSource={data?.data ?? []}
        loading={isLoading}
        rowKey="id"
        pagination={{
          current: page,
          pageSize: limit,
          total: data?.total ?? 0,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total}`,
        }}
        onChange={handleTableChange as () => void}
        onRow={(record) => ({
          onClick: () => navigate(`/interventions/${record.id}`),
          style: { cursor: 'pointer' },
        })}
        size="middle"
      />
    </div>
  );
}
